import React, { useEffect, useId, useRef } from 'react';

const DRIFT_DUR = '3s';

/** Return a random percentage between 15% and 85%. */
const randPct = () => `${15 + Math.floor(Math.random() * 70)}%`;

/**
 * A rounded square with a subtle radial gradient between main-100 and main-300,
 * with a deterministic "random" focal point based on the product ID.
 */
export const ItemColorSquare = ({
  productId,
  size = 13,
  hoverAnimate = false,
}: {
  productId: string;
  size?: number;
  hoverAnimate?: boolean;
}) => {
  const id = useId();
  const svgRef = useRef<SVGSVGElement>(null);

  // Deterministic focal point from product ID
  const hash = productId
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const cx = 25 + (hash % 50); // 25–75%
  const cy = 25 + ((hash * 13) % 50); // 25–75%
  const r = 2;

  useEffect(() => {
    if (!hoverAnimate || !svgRef.current) return undefined;
    const svg = svgRef.current;
    const parent = svg.closest('.group');
    if (!parent) return undefined;

    const handleEnter = () => {
      svg.querySelectorAll('animate').forEach((anim) => {
        const start = anim.getAttribute('data-start') ?? '50%';
        const mid = randPct();
        anim.setAttribute('values', `${start};${mid};${start}`);
        anim.beginElement();
      });
    };

    parent.addEventListener('mouseenter', handleEnter);
    return () => {
      parent.removeEventListener('mouseenter', handleEnter);
    };
  }, [hoverAnimate]);

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
    >
      <defs>
        <radialGradient id={`${id}-rg`} cx={`${cx}%`} cy={`${cy}%`} r="70%">
          <stop offset="0%" stopColor="rgb(var(--colour-main-300))" />
          <stop offset="100%" stopColor="rgb(var(--colour-main-100))" />
          {hoverAnimate && (
            <>
              <animate
                attributeName="cx"
                data-start={`${cx}%`}
                values={`${cx}%;${cx}%;${cx}%`}
                dur={DRIFT_DUR}
                begin="indefinite"
                fill="freeze"
              />
              <animate
                attributeName="cy"
                data-start={`${cy}%`}
                values={`${cy}%;${cy}%;${cy}%`}
                dur={DRIFT_DUR}
                begin="indefinite"
                fill="freeze"
              />
            </>
          )}
        </radialGradient>
      </defs>
      <rect width={size} height={size} rx={r} ry={r} fill={`url(#${id}-rg)`} />
    </svg>
  );
};
